import { actions, persistReducer, localStorage, useIntl, useSelector, useFeatureToggles, ReducersRegistry } from '@grandvisionhq/state';
import * as React from 'react';
import React__default, { useContext } from 'react';
import classnames from 'classnames';
import { Loader, Alert, Tooltip, MediaQuery, Modal, Button, RichTextArea } from '@grandvisionhq/common';
import { Icon } from '@grandvisionhq/icons';
import { gql, useQuery } from '@grandvisionhq/graphql';
import { createSelector } from 'reselect';
import { Hr, Heading } from '@grandvisionhq/elements';

function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}

function extends_() {
    extends_ = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return extends_.apply(this, arguments);
}
function _extends() {
    return extends_.apply(this, arguments);
}

function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}

function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}

// TODO: this should probably have a stricter type than string,
//  but the ShippingMethod enum lives in the checkout package currently
const setPreferredShippingMethod = actions.action('shipping-methods/set-preferred-shipping-method', actions.payload());

const initialState = {
    preferredMethodKey: null
};
const reducer = actions.reducer(initialState, /**
   * Storing user's preferred shipping method.
   * Currently this is the last shipping method selected by the user in the checkout/cart
   */ actions.on(setPreferredShippingMethod, (s, { payload: { shippingMethodKey  }  })=>_objectSpread({}, s, {
        preferredMethodKey: shippingMethodKey
    })
));
const persistConfig = {
    storage: localStorage,
    key: 'grandvision_shipping'
};
var shippingReducer = persistReducer(persistConfig, reducer);

const DeliveryIndicatorContext = /*#__PURE__*/ React__default.createContext({});
const useDeliveryIndicator = ()=>useContext(DeliveryIndicatorContext)
;
function withDeliveryIndicator(Component) {
    const wrappedName = Component.displayName || Component.name || 'Component';
    const WithDeliveryIndicator = (props)=>{
         return React__default.createElement(DeliveryIndicatorContext.Consumer, null, (context)=>{
             return React__default.createElement(Component, _extends({}, props, {
                config: context === null || context === void 0 ? void 0 : context.config
            }));
        });
    };
    WithDeliveryIndicator.displayName = `WithDeliveryIndicator(${wrappedName})`;
    return WithDeliveryIndicator;
}

const DeliveryIndicatorProvider = ({ children , config , deliveryInfo  })=>{
    const value = React.useMemo(()=>({
            config,
            deliveryInfo
        })
    , [
        config,
        deliveryInfo
    ]);
    return /*#__PURE__*/ React.createElement(DeliveryIndicatorContext.Provider, {
        value: value
    }, children);
};

function getProductDelivery(config, availability) {
    const missingDeliveryDays = config.missingInventoryChannelStrategy.expectedDeliveryDays;
    if (typeof availability === 'undefined') {
        return missingDeliveryDays;
    }
    const availableChannels = availability.channels || {};
    const channels = Object.entries(availableChannels);
    if (!channels.length) {
        return missingDeliveryDays;
    }
    // !!! Platform has no support for multiple channels yet.
    const [channelId, channel] = channels[0];
    const channelDeliveryConfig = config.inventoryChannels[channel.key] || config.inventoryChannels[channelId];
    if (!channelDeliveryConfig) {
        console.error(`Inventory channel ${channel.key || channelId} is not configured.`);
        return missingDeliveryDays;
    }
    if (typeof channel.availableQuantity === 'undefined') {
        return missingDeliveryDays;
    }
    const stockKey = channel.availableQuantity > 0 ? 'onStock' : 'outOfStock';
    return channelDeliveryConfig.expectedDeliveryDays[stockKey];
}

const defaultDeliveryResolver = (config, availability1 = [])=>availability1.map((availability)=>getProductDelivery(config, availability)
    )
;

function getDeliveryEstimation(deliveryDays, defaultEstimation) {
    if (!deliveryDays.length) {
        return defaultEstimation;
    }
    let slowest = [
        0,
        0
    ] // [value, index]
    ;
    deliveryDays.forEach((value, index)=>{
        // Strip out anything except numbers and -
        const cleanValue = value.replace(/[^\d-]/g, '');
        // If we have a bound value (like 1-3), take the first number.
        const days = Number(cleanValue.split('-')[0]);
        if (days > slowest[0]) {
            slowest = [
                days,
                index
            ];
        }
    });
    // Return the string representation.
    return deliveryDays[slowest[1]];
}

var DeliveryIndicator$1 = (_param)=>{
    var { labels , loading , error , view ='full' , deliveryMessage , fallbackLabel , tooltipSlot , className  } = _param, rest = _objectWithoutProperties(_param, [
        "labels",
        "loading",
        "error",
        "view",
        "deliveryMessage",
        "fallbackLabel",
        "tooltipSlot",
        "className"
    ]);
    if (loading && !error) {
        return /*#__PURE__*/ React__default.createElement("div", {
            className: "delivery-indicator"
        }, /*#__PURE__*/ React__default.createElement(Loader, null));
    }
    if (error || view !== 'short' && !deliveryMessage) {
        return fallbackLabel ? /*#__PURE__*/ React__default.createElement("div", {
            className: "delivery-indicator"
        }, fallbackLabel) : null;
    }
    const messageElement = /*#__PURE__*/ React__default.createElement("p", {
        className: "delivery-indicator__message"
    }, deliveryMessage);
    const tooltipElement = tooltipSlot ? /*#__PURE__*/ React__default.createElement("div", {
        className: "delivery-indicator__tooltip-slot"
    }, tooltipSlot) : null;
    let content = null;
    switch(view){
        case 'short':
            content = (labels === null || labels === void 0 ? void 0 : labels.deliveryIndication) ? /*#__PURE__*/ React__default.createElement("span", {
                className: "delivery-indicator__message",
                "data-t": "delivery-short"
            }, labels.deliveryIndication) : null;
            break;
        case 'text':
            content = /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, messageElement, tooltipElement);
            break;
        case 'icon':
            content = /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("span", {
                className: "delivery-indicator__icon"
            }, /*#__PURE__*/ React__default.createElement(Icon, {
                name: "delivery"
            })), /*#__PURE__*/ React__default.createElement("div", {
                className: "delivery-indicator__icon-content"
            }, messageElement, tooltipElement));
            break;
        case 'full':
            content = /*#__PURE__*/ React__default.createElement(Alert, {
                type: "info",
                icon: "delivery",
                "data-t": "delivery-alert"
            }, messageElement, tooltipElement);
            break;
        default:
            throw new Error(`Undefined view: ${view}`);
    }
    return /*#__PURE__*/ React__default.createElement("div", {
        "data-t": rest['data-t'],
        className: classnames('delivery-indicator', `delivery-indicator--${view}`, className)
    }, content);
};

const DeliveryIndicatorTooltip = ({ message , view  })=>/*#__PURE__*/ React__default.createElement(Tooltip, {
        id: "delivery-tooltip",
        className: classnames(`delivery-indicator-tooltip--${view}`),
        message: message
    })
;

var DeliveryIndicatorBasicContainer = (_param)=>{
    var { view ='full' , fallbackLabel , availability =[] , deliveryResolver =defaultDeliveryResolver  } = _param, rest = _objectWithoutProperties(_param, [
        "view",
        "fallbackLabel",
        "availability",
        "deliveryResolver"
    ]);
    const { getLabel , getFormattedLabel  } = useIntl();
    const { config  } = useDeliveryIndicator();
    // Can't render without availability and fallback label
    if ((availability === null || availability === void 0 ? void 0 : availability.length) === 0 && !fallbackLabel) {
        return null;
    }
    const deliveries = deliveryResolver(config, availability);
    const deliveryEstimation = getDeliveryEstimation(deliveries, config.missingInventoryChannelStrategy.expectedDeliveryDays);
    const isNextDayDelivery = deliveryEstimation === '1';
    const labels = {
        deliveryIndication: getFormattedLabel('products.deliveryIndicator.shortDeliveryIndication', {
            labelData: {
                days: deliveryEstimation
            }
        })
    };
    let message = fallbackLabel || '';
    if (availability.length) {
        if (config.nextDayDeliveryTimeLimit && isNextDayDelivery) {
            message = `${getFormattedLabel('products.deliveryIndicator.deliveryNextDay', {
                labelData: {
                    timeLimit: config.nextDayDeliveryTimeLimit
                }
            })}`;
        } else if (deliveryEstimation) {
            message = `${getFormattedLabel('products.deliveryIndicator.deliveryIndication', {
                labelData: {
                    days: deliveryEstimation
                }
            })}`;
        }
    }
    return /*#__PURE__*/ React.createElement(DeliveryIndicator$1, _extends({
        view: view,
        labels: labels,
        deliveryMessage: message,
        tooltipSlot: /*#__PURE__*/ React.createElement(DeliveryIndicatorTooltip, {
            message: getLabel('products.deliveryIndicator.deliveryTooltip'),
            view: view
        })
    }, rest));
};

function mapEstimatedDelivery(data, locale) {
    var ref, ref1, ref2, ref3;
    var ref4;
    return {
        deliveryDate: (data === null || data === void 0 ? void 0 : (ref = data.estimatedDeliveryBySkus) === null || ref === void 0 ? void 0 : ref.deliveryDate) ? new Date(data.estimatedDeliveryBySkus.deliveryDate) : undefined,
        shippingMethodDescription: (ref4 = (ref3 = data === null || data === void 0 ? void 0 : (ref1 = data.estimatedDeliveryBySkus) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.shippingMethodDescription) === null || ref2 === void 0 ? void 0 : ref2.find((description)=>description.locale === locale || description.locale === locale.substr(0, 2)
        )) === null || ref3 === void 0 ? void 0 : ref3.value) !== null && ref4 !== void 0 ? ref4 : ''
    };
}

const estimatedDelivery = gql`
  fragment EstimatedDeliveryFragment on EstimatedDelivery {
    deliveryDate
    shippingDate
    shippingMethodDescription {
      locale
      value
      __typename
    }
    __typename
  }
`;

const GET_ESTIMATED_DELIVERY_BY_SKUS = gql`
  query GetEstimatedDeliveryBySkus($input: EstimatedDeliveryBySkuInput!) {
    estimatedDeliveryBySkus(input: $input) {
      ...EstimatedDeliveryFragment
    }
  }
  ${estimatedDelivery}
`;

const useGetEstimatedDeliveryMessage = (items, shippingMethodKey)=>{
    const { dateTime: { formatDate  } , getFormattedLabel , locale ,  } = useIntl();
    const { data , loading , error  } = useQuery(GET_ESTIMATED_DELIVERY_BY_SKUS, {
        variables: {
            input: {
                skuQuantityPairs: items,
                shippingMethodKey
            }
        }
    });
    const estimatedDelivery = mapEstimatedDelivery(data, locale);
    const deliveryMessage = (estimatedDelivery === null || estimatedDelivery === void 0 ? void 0 : estimatedDelivery.deliveryDate) ? getFormattedLabel('products.deliveryIndicator.deliveryIndicationFullDate', {
        labelData: {
            deliveryDate: formatDate(estimatedDelivery.deliveryDate, 'short'),
            deliveryMethod: estimatedDelivery.shippingMethodDescription
        }
    }) : '';
    if (error) {
        console.error(error);
    }
    return {
        deliveryMessage,
        loading,
        error
    };
};

const useGetDispatchDate = (items, shippingMethodKey)=>{
    var ref;
    const { data , loading , error  } = useQuery(GET_ESTIMATED_DELIVERY_BY_SKUS, {
        variables: {
            input: {
                skuQuantityPairs: items,
                shippingMethodKey
            }
        },
        skip: !items || !Array.isArray(items) || !items.length
    });
    // Dates are returned as any by GraphQL, so we need to cast them
    const dispatchDate = data === null || data === void 0 ? void 0 : (ref = data.estimatedDeliveryBySkus) === null || ref === void 0 ? void 0 : ref.shippingDate;
    if (error) {
        console.error(error);
    }
    return {
        dispatchDate,
        loading,
        error
    };
};

const getShipping = (state)=>state.shipping
;

const getPreferredShippingMethod = createSelector(getShipping, (shipping)=>shipping.preferredMethodKey
);

const DeliveryIndicatorModal = ({ deliveryInfo , isModalOpen , onModalClose  })=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const { items =[] , description: description1 , link , linkHint , title: title1  } = deliveryInfo;
    return /*#__PURE__*/ React.createElement(Modal, {
        className: "delivery-modal",
        isOpen: isModalOpen,
        onModalClose: onModalClose,
        showIcon: true,
        title: title1,
        footer: link && /*#__PURE__*/ React.createElement(React.Fragment, null, linkHint && /*#__PURE__*/ React.createElement("p", null, linkHint), /*#__PURE__*/ React.createElement(Button, {
            caption: link.label,
            href: link.url,
            hrefTarget: link.target === 'external' ? '_blank' : undefined,
            size: "large"
        }))
    }, /*#__PURE__*/ React.createElement("div", {
        className: "delivery-modal__container"
    }, description1 && /*#__PURE__*/ React.createElement(RichTextArea, {
        className: "delivery-modal__description",
        content: description1
    }), items.map(({ description , id , image , price , title  })=>/*#__PURE__*/ React.createElement("div", {
            key: id,
            className: "delivery-modal-item"
        }, /*#__PURE__*/ React.createElement(Hr, {
            className: "delivery-modal-item__horizontal-rule"
        }), /*#__PURE__*/ React.createElement("div", {
            className: "delivery-modal-item__header"
        }, title && /*#__PURE__*/ React.createElement(Heading, {
            component: "h2",
            variant: "smallest"
        }, title), price && /*#__PURE__*/ React.createElement(Heading, {
            component: "h3",
            variant: "smallest"
        }, "\xa0\xb7 ", price)), /*#__PURE__*/ React.createElement("div", {
            className: "delivery-modal-item__content"
        }, image && /*#__PURE__*/ React.createElement("div", {
            className: "delivery-modal-item__image-container"
        }, /*#__PURE__*/ React.createElement("img", {
            className: "delivery-modal-item__image",
            src: isSmallScreen ? image.mobile.url : image.original.url,
            alt: image.alt
        })), description && /*#__PURE__*/ React.createElement(RichTextArea, {
            className: "delivery-modal-item__description",
            content: description
        })))
    )));
};

const DeliveryIndicatorModalToggle = ({ onClick  })=>/*#__PURE__*/ React.createElement("button", {
        // TODO: create a proper aria-label with BR Label
        // aria-label="tooltip"
        className: "tooltip__button",
        onClick: onClick
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "questionmark"
    }))
;

const DeliveryIndicatorModalContainer = ()=>{
    const { deliveryInfo  } = useDeliveryIndicator();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const onModalOpen = React.useCallback(()=>{
        setIsModalOpen(true);
    }, []);
    const onModalClose = React.useCallback(()=>setIsModalOpen(false)
    , []);
    if (!deliveryInfo) return null;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(DeliveryIndicatorModalToggle, {
        onClick: onModalOpen
    }), /*#__PURE__*/ React.createElement(DeliveryIndicatorModal, {
        deliveryInfo: deliveryInfo,
        isModalOpen: isModalOpen,
        onModalClose: onModalClose
    }));
};

var DeliveryIndicatorFullDateContainer = (_param)=>{
    var { items =[] , fallbackLabel , view ='full'  } = _param, rest = _objectWithoutProperties(_param, [
        "items",
        "fallbackLabel",
        "view"
    ]);
    const preferredShippingMethod = useSelector(getPreferredShippingMethod);
    const { deliveryMessage , loading , error  } = useGetEstimatedDeliveryMessage(items, preferredShippingMethod);
    return /*#__PURE__*/ React__default.createElement(DeliveryIndicator$1, _extends({
        deliveryMessage: deliveryMessage,
        loading: loading,
        error: !!error,
        view: view,
        fallbackLabel: fallbackLabel,
        tooltipSlot: /*#__PURE__*/ React__default.createElement(DeliveryIndicatorModalContainer, null)
    }, rest));
};

var DeliveryIndicator = (_param)=>{
    var { items =[] , deliveryResolver , availability , fallbackLabel  } = _param, rest = _objectWithoutProperties(_param, [
        "items",
        "deliveryResolver",
        "availability",
        "fallbackLabel"
    ]);
    const { isDeliveryFullDateEnabled  } = useFeatureToggles();
    const { getLabel  } = useIntl();
    const fallbackLabelValue = fallbackLabel !== null && fallbackLabel !== void 0 ? fallbackLabel : getLabel('products.deliveryIndicator.fallbackMessage');
    // Full date delivery indicator that fetches a calculated delivery estimate from the backend
    if (isDeliveryFullDateEnabled && (items === null || items === void 0 ? void 0 : items.length) > 0) {
        return /*#__PURE__*/ React__default.createElement(DeliveryIndicatorFullDateContainer, _extends({
            items: items,
            fallbackLabel: fallbackLabelValue
        }, rest));
    }
    return(// Basic delivery indicator that renders delivery estimate based on stock levels
    /*#__PURE__*/ React__default.createElement(DeliveryIndicatorBasicContainer, _extends({
        deliveryResolver: deliveryResolver,
        availability: availability,
        fallbackLabel: fallbackLabelValue
    }, rest)));
};

ReducersRegistry.register('shipping', shippingReducer);

export { DeliveryIndicator, DeliveryIndicatorProvider, defaultDeliveryResolver, getPreferredShippingMethod, setPreferredShippingMethod, useDeliveryIndicator, useGetDispatchDate, useGetEstimatedDeliveryMessage, withDeliveryIndicator };
//# sourceMappingURL=index.js.map
